/* GASSISA
=========================================================================================
* Descripción: 
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 23/11/2024 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Protección anti doble click en acciones críticas
*      3       | 01/06/2026 | Lic. José Luis Aragón Cervantes  | Eliminaciones de venta en otras acciones
* ---------------------------------------------------------------------------------------
*/

import { Fragment } from 'react';
import { Box, Grid, IconButton, Tooltip, ClickAwayListener, Typography } from '@mui/material';
import { useLayout } from '../../context/System/LayoutContext';
import CloseIcon from '@mui/icons-material/Close';
import DirectionsIcon from '@mui/icons-material/Directions';
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import VerifiedIcon from "@mui/icons-material/Verified";
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import RefreshIcon from "@mui/icons-material/Refresh";
import PrintIcon from '@mui/icons-material/Print';
import NextPlanIcon from '@mui/icons-material/NextPlan';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import { useNavigate } from 'react-router-dom';

import { useVentas } from "../../context/Ventas/VentasContext";
import { useSesion } from "../../context/System/SesionContext";
import { useDoubleClickPrevention } from "../../hooks/useDoubleClickPrevention";

export default function MnuOtherActions({ statusCajaActual, onAbrirAperturaCaja, onAbrirCierreCaja, onAbrirAbonoPrestamoVenta, onAbrirPrestamoEmpleado }) {
    const navigate = useNavigate();
    const { definicionMenu } = useSesion();
    
    const esDevolucionesVisible = Object.keys(definicionMenu).some((modulo) => {
        if (modulo.startsWith("_")) return false;
        const paginas = definicionMenu[modulo]._paginas || {};
        return Object.values(paginas).some((pagina) => pagina.path === "/devoluciones" && pagina.mostrar);
    });

    const { 
        handleAbrirModalJalarPreVentaCotizacion,
        handleAbrirModalGenerarApartado,
        handleCerrarMenuModal,
        openModal,
        handleClickEliminarPreVentaDesdeVenta,
        handleClickLimpiarPreventas,
        handleAbrirRetiroCapital,
        statusKeyActions,
        setStatusKeyActions,
        statusWaiting,
        handleGuardarVentaComoCotizacion,
        handleClickEliminarVenta,
        handleClickEliminarPartidaVentas,
        partidaSeleccionada,
        listaRemisionTemporal,
        handleClickDescuentoVenta,
    } = useVentas();
    const { handleOpenAlert } = useLayout();
    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

    const handleClickAway = () => {
        handleCerrarMenuModal();
    };

    const handleSwitchModoApertura=(modo) => {     
        handleAbrirModalJalarPreVentaCotizacion(modo); 
    };

    const handleRetiroCapital = () => {
        if (!statusCajaActual?.abierta) {
            handleOpenAlert("No se puede realizar el retiro de efectivo mientras la caja esté cerrada", "warning");
            return;
        }
        handleCerrarMenuModal();
        handleAbrirRetiroCapital();
    };

    const handleDescuentos = () => {
        handleCerrarMenuModal();
        handleClickDescuentoVenta();
    };

    const isDisabled = statusWaiting || isProcessing;
    const cajaCerrada = !statusCajaActual?.abierta;
    const accionesBloqueadas = isDisabled || cajaCerrada;
    const tieneVenta = Array.isArray(listaRemisionTemporal) && listaRemisionTemporal.length > 0;
    const ventaBloqueada = accionesBloqueadas || !tieneVenta;
    const cajaCerradaOBloqueada = accionesBloqueadas;

    return (
        openModal && statusKeyActions && (
            <ClickAwayListener onClickAway={handleClickAway}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '280px',
                        height: '100%',
                        maxHeight: '100vh',
                        backgroundColor: '#191970', // Azul para menus
                        boxShadow: 3,
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        zIndex: 1200,
                        overflowY: 'auto',
                        transform: openModal ? 'translateX(0)' : 'translateX(100%)',
                        transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
                    }}
                >
                    {/* Botón de cierre */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <IconButton
                            onClick={handleCerrarMenuModal}
                            disabled={isDisabled}
                            sx={{
                                color: isDisabled ? 'rgba(255,255,255,0.3)' : 'white',
                                '&:hover': {
                                    color: isDisabled ? 'rgba(255,255,255,0.3)' : '#ff4136',
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {/* Contenido del detalle con scroll (ya son muchas acciones) */}
                    <Fragment>
                        <Grid container spacing={2} sx={{ overflowY: 'auto', flexWrap: 'nowrap', flexDirection: 'column', pb: 2, alignItems: 'flex-start' }}>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => handleSwitchModoApertura("PREVENTA")}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#2ecc40',
                                        },
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                    }}
                                >
                                    <NextPlanIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Jalar Preventas {`{Alt+J}`}
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => handleSwitchModoApertura("COTIZACION")}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#2ecc40',
                                        },
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                    }}
                                >
                                    <NextPlanIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Jalar Cotizaciones {`{Alt+O}`}
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(handleAbrirModalGenerarApartado)}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#2ecc40',
                                        },
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                    }}
                                >
                                    <NextPlanIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Generar Apartado {`{Ctrl+Alt+A}`}
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(handleClickEliminarPreVentaDesdeVenta)}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#2ecc40',
                                        },
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                    }}
                                >
                                    <NextPlanIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Eliminar Full Venta y Preventa
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(() => handleClickEliminarPartidaVentas(partidaSeleccionada?.idpartida))}
                                    disabled={ventaBloqueada || !partidaSeleccionada?.idpartida}
                                    sx={{
                                        color: ventaBloqueada || !partidaSeleccionada?.idpartida ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: ventaBloqueada || !partidaSeleccionada?.idpartida ? 'rgba(255,255,255,0.3)' : '#ff4136',
                                        },
                                    }}
                                >
                                    <DeleteIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: ventaBloqueada || !partidaSeleccionada?.idpartida ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Eliminar partida venta {`{Alt+F7}`}
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(handleClickEliminarVenta)}
                                    disabled={cajaCerradaOBloqueada}
                                    sx={{
                                        color: cajaCerradaOBloqueada ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: cajaCerradaOBloqueada ? 'rgba(255,255,255,0.3)' : '#ff4136',
                                        },
                                    }}
                                >
                                    <DeleteIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: cajaCerradaOBloqueada ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Eliminar venta {`{Alt+F12}`}
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(handleClickLimpiarPreventas)}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#ff4136',
                                        },
                                    }}
                                >
                                    <DeleteIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Eliminar preventas(Todo)
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(() => {
                                        handleCerrarMenuModal();
                                        handleGuardarVentaComoCotizacion();
                                    })}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#2ecc40',
                                        },
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                    }}
                                >
                                    <SaveAltIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Terminar como Cotización
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(() => { handleCerrarMenuModal(); onAbrirAperturaCaja?.(); })}
                                    sx={{
                                        color: 'white',
                                        '&:hover': {
                                            color: '#2ecc40',
                                        },
                                    }}
                                >
                                    <LockOpenIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Apertura de Caja
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(() => { handleCerrarMenuModal(); onAbrirCierreCaja?.(); })}
                                    sx={{
                                        color: 'white',
                                        '&:hover': {
                                            color: '#ff4136',
                                        },
                                    }}
                                >
                                    <LockIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Cierre de Caja
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(() => { handleCerrarMenuModal(); onAbrirPrestamoEmpleado?.(); })}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#2ecc40',
                                        },
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                    }}
                                >
                                    <AccountBalanceWalletIcon />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Préstamo empleado
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(() => { handleCerrarMenuModal(); onAbrirAbonoPrestamoVenta?.(); })}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#2ecc40',
                                        },
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                    }}
                                >
                                    <PriceCheckIcon />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Abono préstamo empleado
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(handleRetiroCapital)}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#f39c12',
                                        },
                                    }}
                                >
                                    <RemoveCircleOutlineIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Retiro de Efectivo
                                    </Typography>
                                </IconButton>
                            </Grid>
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => executeWithProtection(handleDescuentos)}
                                    disabled={accionesBloqueadas || !tieneVenta}
                                    sx={{
                                        color: accionesBloqueadas || !tieneVenta ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas || !tieneVenta ? 'rgba(255,255,255,0.3)' : '#2ecc40',
                                        },
                                    }}
                                >
                                    <PriceCheckIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas || !tieneVenta ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Descuentos {`{Alt+F3}`}
                                    </Typography>
                                </IconButton>
                            </Grid>
                            {esDevolucionesVisible && (
                            <Grid item xs={12}>
                                <IconButton
                                    onClick={() => {
                                        handleCerrarMenuModal();
                                        navigate("/devoluciones");
                                    }}
                                    disabled={accionesBloqueadas}
                                    sx={{
                                        color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                        '&:hover': {
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : '#9b59b6',
                                        },
                                    }}
                                >
                                    <ReplayIcon />
                                    <Typography 
                                        variant="body2" 
                                        sx={{
                                            color: accionesBloqueadas ? 'rgba(255,255,255,0.3)' : 'white',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Devoluciones {`{Ctrl+Alt+D}`}
                                    </Typography>
                                </IconButton>
                            </Grid>
                            )}
                        </Grid>
                    </Fragment>
                </Box>
            </ClickAwayListener>
        )
    );
}


